/**
 * ModuleRecordsPage — F12.A T12.A9 + Kreator Listy (parity z listami systemowymi).
 *
 * Lista rekordów modułu custom + create/edit/delete.
 * Routing: `/modules/:slug` (tenant scope).
 *
 * Widok tabeli jest teraz 1:1 z listą Kontrahentów (`Contractors.tsx`):
 *  - wyszukiwarka + filtry per kolumna (wiersz filtrów + popup w nagłówku) + pasek
 *    filtrów `ListFilterBar` + szybkie filtry `QuickFilterBar`,
 *  - filtry kolumn (`headerFilters`) idą do backendu jako pojedyncza tablica
 *    `headerFilters` (aplikowana w WHERE); w KAŻDEJ kolumnie z danymi wiersz filtrów ma
 *    działającą kontrolkę wg typu pola, a kolumna bez filtra ma pustą komórkę (bez
 *    generycznego pola „Szukaj…”) — reguły w `lib/custom-modules/module-list-columns.tsx`,
 *  - kolumny / szerokości / kolejność persystowane przez `useTableLayout`.
 *
 * Akcje zbiorcze (PHASE 25) — wspólny silnik przez `useListActions` z kartoteką
 * `generic`: menu „Akcje" nad listą + prawy klik + kebab w wierszu; katalog pokazuje
 * TYLKO akcje uniwersalne (archiwizuj/przywróć/eksport/import/odśwież), nigdy KSeF/FK.
 * Archiwizacja = miękkie usunięcie rozgłoszone DELETE po ID na endpoint modułu,
 * przywrócenie = POST `.../restore`. Zastąpiło bespoke pasek masowy + `ListOperationsMenu`.
 *
 * Zachowane (specyfika modułów custom, nie ma w Kontrahentach):
 *  - przełącznik widoku (tabela / kanban / galeria / kalendarz) + ich rendery,
 *  - zakładki kategorii (typów rekordów),
 *  - „+ Nowy rekord" → RecordFormPage, „Konfiguruj" → builder, „Typy rekordów".
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  listExportPath,
  type ModuleRecordsViewMode,
  moduleRecordsViewModeSchema,
} from '@saas/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, CalendarDays, Grid3X3, LayoutList, Pencil, Plus, Trello } from 'lucide-react';

import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import {
  type ColumnHeaderFilters,
  ConfigurableTable,
  KanbanBoard,
  ListActionsMenu,
  ListBuilderMenu,
  ListFilterBar,
  ListTopWidgets,
  ListViewModeToggle,
  QuickFilterBar,
  RowActionsMenu,
  useTableLayout,
  type ViewModeOption,
} from '@/components/configurable-table';
import { buildEndpointUrl } from '@/components/configurable-table/useDistinctValues';
import { useServerListControls } from '@/components/configurable-table/useServerListControls';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import type { ModuleField } from '@/components/fields/FieldRegistry';
import { CalendarView } from '@/components/modules/CalendarView';
import { useColumnConfig } from '@/components/modules/ColumnPickerPanel';
import { GalleryView } from '@/components/modules/GalleryView';
import type { ModuleRecord } from '@/components/modules/record-types';
import { RecordImportModal } from '@/components/modules/RecordImportModal';
import { Spinner } from '@/components/Spinner';
import { useTabsOptional } from '@/contexts/TabContext';
import { useTenantAuth } from '@/contexts/TenantAuthContext';
import { useToast } from '@/contexts/ToastContext';
import { useActiveMenuItem } from '@/hooks/useActiveMenuItem';
import { useForcedListLayoutId } from '@/hooks/useForcedListLayoutId';
import { useListingPreferences } from '@/hooks/useListingPreferences';
import { useWithMenuItem } from '@/hooks/useWithMenuItem';
import { api } from '@/lib/api';
import {
  buildModuleKanbanColumns,
  buildModuleListColumns,
  buildModuleListHeaderFilters,
  moduleHasNumbering,
  moduleListTableId,
} from '@/lib/custom-modules/module-list-columns';
import {
  moduleExportKeys,
  moduleExportTextOverrides,
} from '@/lib/custom-modules/module-list-export';
import { useModuleListFilterOptions } from '@/lib/custom-modules/use-module-list-filter-options';
import { useListActions } from '@/lib/list-actions/useListActions';
import { exportColumnsFromTable } from '@/lib/list-export/cell-text';
import type { ListExportResponse } from '@/lib/list-export/run-export';
import { useListCsvExport } from '@/lib/list-export/useListCsvExport';
import { baseFiltersToHeaderFilters, baseSortToParams } from '@/lib/menu/menu-item-view-runtime';

interface LayoutElement {
  id: string;
  type: 'field';
  fieldId: string;
  width: 1 | 2 | 3;
  settings: { label?: string | null; required?: boolean | null };
}

interface LayoutSection {
  id: string;
  title: string | null;
  columns: 1 | 2 | 3;
  elements: LayoutElement[];
}

interface CustomModuleLayout {
  id: string;
  layoutType: 'form' | 'list';
  isDefault: boolean;
  layoutConfig: { sections: LayoutSection[] };
  /** T12.H.11 — przypisanie do kategorii. NULL = uniwersalny. */
  categoryId?: string | null;
  /** T12.H.11 — przypisanie do ról. Puste = dla wszystkich. */
  roleIds?: string[];
}

interface ModuleDetail {
  id: string;
  slug: string;
  name: string;
  fields: ModuleField[];
  layouts: CustomModuleLayout[];
}

interface Category {
  id: string;
  name: string;
  slug: string;
  color: string;
  isDefault: boolean;
  showAsTab: boolean;
  numberingConfig?: { enabled: boolean } | null;
}

interface RecordsResponse {
  records: ModuleRecord[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
  /**
   * Slugi kolumn sortowalnych serwerowo (silnik list) — kolumny systemowe
   * (`created_at`/`updated_at`/`record_name`/`position`) + slugi pól modułu
   * jednokolumnowych. Composite (address/daterange/location) i `record_number`
   * celowo NIE są tu — opcjonalny, bo Kanban/Calendar/RecordFormPage/RelationPicker/
   * ScheduleGrid wołają ten sam endpoint i ignorują to pole.
   */
  meta?: { sortable: string[] };
}

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

export function ModuleRecordsPage() {
  const { slug: moduleSlug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { me } = useTenantAuth();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  // T12.D.cm — cross-module action prefill (gdy user kliknął „Wystaw fakturę" w innym module)
  const crossModulePrefill = useMemo(() => {
    const createNew = searchParams.get('createNew') === '1';
    if (!createNew) return null;
    const prefillRaw = searchParams.get('prefill');
    let prefill: Record<string, unknown> | null = null;
    if (prefillRaw) {
      try {
        prefill = JSON.parse(prefillRaw) as Record<string, unknown>;
      } catch {
        prefill = null;
      }
    }
    return {
      prefill,
      sourceRecordId: searchParams.get('source'),
      sourceModuleId: searchParams.get('sourceModule'),
      categoryId: searchParams.get('category'),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString()]);

  // F12 PR2 cz.5.2 etap 6 — search-columns/includeArchived/viewMode żyją per-user
  // w backendzie (cross-machine sync). Klucz `module:<slug>` żeby uniknąć kolizji
  // z systemowymi (które używają surowych slugów employees/companies/...).
  const {
    config: listingPrefs,
    setConfig: setListingPrefs,
    isLoading: listingPrefsLoading,
  } = useListingPreferences(`module:${moduleSlug}`, {
    searchColumns: ['record_name'],
    includeArchived: false,
  });

  // Wyszukiwarka (lokalny stan kontrolera + debounce 300ms — wzorzec z Kontrahentów).
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);

  const searchColumns =
    Array.isArray(listingPrefs.searchColumns) && listingPrefs.searchColumns.length > 0
      ? (listingPrefs.searchColumns as string[])
      : ['record_name'];
  const includeArchived = !!listingPrefs.includeArchived;
  const setIncludeArchived = (next: boolean) => setListingPrefs({ includeArchived: next });

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // Kolumny FAKTYCZNIE widoczne w tabeli (kolejność z ekranu) — zasila eksport CSV (E16).
  // `null` przed pierwszym zgłoszeniem tabeli → eksport bierze kolumny domyślnie widoczne.
  const [visibleColumnIds, setVisibleColumnIds] = useState<string[] | null>(null);
  const [activeTypeId, setActiveTypeId] = useState<string | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [pendingConfirm, setPendingConfirm] = useState<{
    title: string;
    message: string;
    onConfirm: () => void;
  } | null>(null);

  // `meta.sortable` z ostatniej odpowiedzi — trzymane w stanie (nie czytane wprost
  // z `recordsQuery.data`), żeby sortowalność nagłówków nie migała między fetchami
  // (react-query czyści `data` na czas refetchu przy zmianie queryKey). Zasilane
  // efektem podpiętym pod `recordsQuery` (patrz niżej, po jej definicji).
  const [sortableSlugs, setSortableSlugs] = useState<string[]>([]);

  // Filtry per kolumna — z wiersza filtrów pod nagłówkami i z popupu w nagłówku (Kreator
  // Listy). Controlled → ConfigurableTable nie filtruje lokalnie, backend aplikuje w WHERE.
  // Lista NIE kontroluje `columnFilters`: wiersz filtrów nie rysuje więc generycznego pola
  // „Szukaj…" (filtrowałoby tylko pobraną stronę) — każda kolumna ma kontrolkę typowaną albo
  // pustą komórkę (patrz `moduleFieldFilter`).
  const [headerFilters, setHeaderFilters] = useState<ColumnHeaderFilters>({});

  // T12.D.cm + T12.H.12 — legacy fallback: stary URL z ?createNew=1 (bookmarki,
  // stare maile) → przekieruj na nowy /records/new z zachowanymi query params.
  useEffect(() => {
    if (!crossModulePrefill) return;
    const params = new URLSearchParams();
    if (crossModulePrefill.prefill) {
      params.set('prefill', JSON.stringify(crossModulePrefill.prefill));
    }
    if (crossModulePrefill.sourceRecordId) {
      params.set('source', crossModulePrefill.sourceRecordId);
    }
    if (crossModulePrefill.sourceModuleId) {
      params.set('sourceModule', crossModulePrefill.sourceModuleId);
    }
    if (crossModulePrefill.categoryId) {
      params.set('category', crossModulePrefill.categoryId);
    }
    navigate(`/modules/${moduleSlug}/records/new?${params.toString()}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crossModulePrefill]);

  // Pobranie modułu (do dynamicznych kolumn + form layout)
  const moduleQuery = useQuery({
    queryKey: ['module-by-slug', moduleSlug],
    queryFn: async () => {
      const res = await api.get<{ module: ModuleDetail }>(`/modules/${moduleSlug}`);
      return res.data.module;
    },
    enabled: !!moduleSlug,
  });

  const moduleId = moduleQuery.data?.id ?? '';

  // Pozycja menu tenanta, przez którą wszedł klient (`?mi=<id>`). Gdy ma przypisany
  // layout listy / filtry / sortowanie stałe — honorujemy je w runtime.
  const activeMenuItem = useActiveMenuItem();
  const menuBaseFilters = activeMenuItem?.view?.baseFilters;
  const menuBaseSort = activeMenuItem?.view?.baseSort;

  // Wymuszony layout listy z tej pozycji menu — wydzielone do współdzielonego hooka
  // (kartoteka-layout-per-menu), reużywanego też przez listy kartotek systemowych.
  // tableId z sufiksem `::layout-<id>` blokuje przełączanie layoutu (useTableLayout).
  const forcedListLayoutId = useForcedListLayoutId(moduleSlug);

  const tableId = moduleListTableId(moduleId, forcedListLayoutId);
  // Identyfikator PODPOWIEDZI filtra (`distinctTableId`, klucze `['distinct-values', …]`): zawsze
  // `module-<id>`, bez sufiksu układu wymuszonego z menu — z sufiksem adres podpowiedzi byłby
  // błędny (`/modules/<id>::layout-<lid>/records/distinct`), a unieważnienia mijałyby klucz.
  const distinctTableId = moduleListTableId(moduleId);

  // Dokleja `?mi=<id>` do nawigacji do formularza/detalu, żeby formularz znał kontekst
  // pozycji menu (viewRef). Zachowuje istniejący query string. Współdzielony hook
  // (kartoteka-layout-per-menu) — ten sam wzorzec co listy kartotek systemowych.
  const withMenuItem = useWithMenuItem();

  // Etykieta karty = nazwa kartoteki (zamiast generycznego „Moduły"). Trafiamy w kartę
  // po basePath (nie w aktywną) — panele są keep-alive. Poza powłoką kart (testy)
  // `tabCtx` jest null → no-op.
  const tabCtx = useTabsOptional();
  useEffect(() => {
    const name = moduleQuery.data?.name;
    if (!tabCtx || !name) return;
    const basePath = `/modules/${moduleSlug}`;
    const myTab = tabCtx.tabs.find((tt) => tt.basePath === basePath);
    if (myTab && myTab.label !== name) tabCtx.updateTabLabel(myTab.id, name);
  }, [tabCtx, moduleQuery.data?.name, moduleSlug]);

  // Pobranie typów rekordów
  const categoriesQuery = useQuery({
    queryKey: ['module-categories', moduleId],
    queryFn: async () => {
      const res = await api.get<{ categories: Category[] }>(`/modules/${moduleId}/categories`);
      return res.data.categories;
    },
    enabled: !!moduleId,
  });

  // Konfiguracja listy z Kreatora (list_config): szybkie filtry, ustawienia
  // strony, etykiety standardowych przycisków, własne przyciski nawigacyjne.
  const { layout: listLayout, isLoading: layoutLoading } = useTableLayout(tableId, {
    enabled: !!moduleId,
  });
  const quickFilters = useMemo(
    () => listLayout?.layoutConfig?.quickFilters ?? [],
    [listLayout?.layoutConfig?.quickFilters],
  );
  const pageConfig = listLayout?.layoutConfig?.pageConfig;

  // Paginacja/sort — wspólny hook z listami systemowymi (Kontrahenci/Pracownicy): to on
  // odblokowuje żywy dropdown „Wierszy na stronę" w `ConfigurableTable`. Rozmiar strony z
  // ustawień widoku (Kreator Listy) przejmuje sama tabela — wspólnie dla wszystkich list.
  const controls = useServerListControls();
  const buttonsConfig = useMemo(
    () => listLayout?.layoutConfig?.buttonsConfig ?? [],
    [listLayout?.layoutConfig?.buttonsConfig],
  );
  // Konfiguracja Kanban — ujednolicona z listami systemowymi (`list_config.kanbanConfig`).
  // `groupByFieldId` to **slug** pola grupującego (parytet z migracją starego
  // `kanban_config`). Renderem jest wspólny `KanbanBoard` (ten sam co w Kreatorze).
  const kanbanConfig = listLayout?.layoutConfig?.kanbanConfig;
  const hasKanban = Boolean(kanbanConfig?.groupByFieldId);
  // Konfiguracja kalendarza (`list_config.calendarConfig`) — pole startu + opcjonalny
  // koniec (zakres) + opcjonalny status (kolor wpisów). Sluggi pól, jak w kanban.
  const calendarConfig = listLayout?.layoutConfig?.calendarConfig;
  // Domyślny tryb STRONY z Kreatora Listy (`list_config.defaultModuleRecordsViewMode`)
  // — fallback TYLKO gdy user nie ma jeszcze własnej (per-user) preferencji widoku
  // (np. moduł „Urlopy" otwiera się od razu w kalendarzu zamiast tabeli). Per-user
  // preferencja z `listingPrefs.viewMode`, gdy istnieje, zawsze wygrywa nad configiem.
  const configDefaultViewMode = listLayout?.layoutConfig?.defaultModuleRecordsViewMode;
  // Walidacja runtime zapisanej preferencji — wartość z backendu jest `unknown`;
  // nieznana/uszkodzona wartość degraduje do fallbacku zamiast być zaufana na ślepo.
  const savedViewModeParsed = moduleRecordsViewModeSchema.safeParse(listingPrefs.viewMode);
  const savedViewMode = savedViewModeParsed.success ? savedViewModeParsed.data : undefined;
  // Dopóki zapytanie o per-user preferencję jeszcze się ładuje, NIE stosuj configDefault —
  // inaczej user z zapisanym „table" widzi błysk configowego widoku (np. kalendarza)
  // zanim sieć odpowie.
  const viewMode =
    savedViewMode ?? (listingPrefsLoading ? undefined : configDefaultViewMode) ?? 'table';
  const setViewMode = (next: 'table' | 'kanban' | 'gallery' | 'calendar') =>
    setListingPrefs({ viewMode: next });
  // Etykieta standardowego przycisku — własna z Kreatora lub domyślna.
  const labelFor = (actionId: string, fallback: string) =>
    buttonsConfig.find((b) => b.actionId === actionId)?.customLabel?.trim() || fallback;
  // Własne przyciski (pasek / wiersz / masowe) rozkłada wspólny hook akcji listy.
  const [activeQuickFilterId, setActiveQuickFilterId] = useState<string | null>(null);
  useEffect(() => {
    if (activeQuickFilterId === null && quickFilters.length > 0) {
      const def = quickFilters.find((q) => q.isDefault);
      if (def) setActiveQuickFilterId(def.id);
    }
  }, [quickFilters, activeQuickFilterId]);

  // Pobranie rekordów (po module.id)
  const recordsQuery = useQuery({
    queryKey: [
      'module-records',
      moduleSlug,
      controls.page,
      controls.pageSize,
      debouncedSearch,
      searchColumns,
      includeArchived,
      activeTypeId,
      headerFilters,
      activeQuickFilterId,
      // Filtry/sort stałe z pozycji menu — sygnatura w kluczu, żeby przełączenie
      // pozycji odświeżyło listę (same filtry idą poza reactowym stanem, w queryFn).
      menuBaseFilters,
      menuBaseSort,
      controls.sortColumnId,
      controls.sortDirection,
    ],
    queryFn: async () => {
      const params = buildListFilterParams();
      // Backend modułów custom liczy stronicowanie po `page` (1-indeksowana) + `limit`
      // (LIMIT/OFFSET po stronie silnika raw-SQL) — `controls.page` z hooka jest
      // 0-indeksowany (kontrakt wspólny z Kontrahentami/Pracownikami, gdzie backend
      // liczy `offset` wprost), stąd `+ 1` na granicy tego zapytania.
      params.set('page', String(controls.page + 1));
      params.set('limit', String(controls.pageSize));

      const res = await api.get<RecordsResponse>(`/modules/${moduleId}/records?${params}`);
      return res.data;
    },
    enabled: !!moduleId,
  });

  // Parametry filtrów listy BEZ stronicowania — wspólne dla zapytania listy i eksportu CSV
  // (E16: plik zawiera dokładnie to, co lista). Zwykła funkcja, nie hook: czyta kolumny i pola
  // zdefiniowane niżej w komponencie, a wołana jest dopiero przy pobieraniu (po renderze).
  function buildListFilterParams(): URLSearchParams {
    const params = new URLSearchParams();
    // Sort: kliknięcie nagłówka (`controls.sortColumnId`, akcja usera) ma pierwszeństwo
    // nad sortem stałym z pozycji menu — jawny wybór usera wygrywa. Brak obu → serwer
    // stosuje własny domyślny porządek (position ASC, created_at DESC), więc NIE
    // wysyłamy `?sort=` dopóki user czegoś nie kliknie.
    if (controls.sortColumnId) {
      params.set('sort', controls.sortColumnId);
      params.set('order', controls.sortDirection);
    } else {
      const menuSort = baseSortToParams(menuBaseSort);
      if (menuSort) {
        params.set('sort', menuSort.sort);
        params.set('order', menuSort.order);
      }
    }
    if (debouncedSearch) {
      params.set('search', debouncedSearch);
      if (searchColumns.length > 0) params.set('searchColumns', searchColumns.join(','));
    }
    if (includeArchived) params.set('archived', 'true');
    if (activeTypeId) params.set('type_id', activeTypeId);

    // Filtry idą do backendu jako pojedyncza tablica `headerFilters`:
    // (1) z wiersza filtrów i popupu w nagłówku (mode=values/condition/range — kształt
    //     zależy od typu pola, patrz `moduleFieldFilter`: adres/tekst = condition+contains,
    //     data/zakres dat/data i godzina = condition+eq, lista wyboru/użytkownik/relacja =
    //     values; opcja „(Puste)” użytkownika/relacji idzie jako `null` w `selectedValues` —
    //     zamiana po typie pola z WSZYSTKICH aktywnych pól, więc działa też po ukryciu
    //     kolumny)
    //     + (2) aktywny szybki filtr (preset).
    // Jedna wspólna droga ze stroną i sekcją kokpitu: `buildModuleListHeaderFilters`.
    const activeQf = quickFilters.find((q) => q.id === activeQuickFilterId);
    const merged = buildModuleListHeaderFilters({
      columns: tableColumns,
      fields: activeFields,
      headerFilters,
      quickFilterConditions: activeQf?.filters ?? [],
    });

    // Filtry stałe z pozycji menu — dokładane WYŁĄCZNIE tutaj (poza reactowym
    // stanem headerFilters), więc są niewidoczne i nieusuwalne dla usera; łączone
    // AND z jego własnymi filtrami. Pola adresowane slugiem wprost (bez lookupu
    // column-id), więc nie wymagają tableColumns.
    merged.push(...baseFiltersToHeaderFilters(menuBaseFilters, me?.user.id));

    if (merged.length > 0) {
      params.set('headerFilters', JSON.stringify(merged));
    }
    return params;
  }

  // Zasila `sortableSlugs` z ostatniej udanej odpowiedzi — patrz komentarz przy
  // deklaracji stanu (chroni przed miganiem sortowalności nagłówków w trakcie fetchu).
  useEffect(() => {
    if (recordsQuery.data?.meta?.sortable) {
      setSortableSlugs(recordsQuery.data.meta.sortable);
    }
  }, [recordsQuery.data]);

  // Kanban: pobiera wszystkie rekordy bez paginacji (max 500)
  const kanbanRecordsQuery = useQuery({
    queryKey: ['module-records-kanban', moduleSlug, includeArchived, activeTypeId],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: '500' });
      if (includeArchived) params.set('archived', 'true');
      if (activeTypeId) params.set('type_id', activeTypeId);
      const res = await api.get<RecordsResponse>(`/modules/${moduleId}/records?${params}`);
      return res.data.records;
    },
    enabled: !!moduleId && viewMode === 'kanban',
  });

  // Calendar: pobiera wszystkie rekordy bez paginacji (max 500)
  const calendarRecordsQuery = useQuery({
    queryKey: ['module-records-calendar', moduleSlug, includeArchived, activeTypeId],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: '500' });
      if (includeArchived) params.set('archived', 'true');
      if (activeTypeId) params.set('type_id', activeTypeId);
      const res = await api.get<RecordsResponse>(`/modules/${moduleId}/records?${params}`);
      return res.data.records;
    },
    enabled: !!moduleId && viewMode === 'calendar',
  });

  const deleteMutation = useMutation({
    mutationFn: async (recordId: string) => {
      await api.delete(`/modules/${moduleId}/records/${recordId}`);
      return recordId;
    },
    onSuccess: () => {
      toast.show({ variant: 'success', title: t('tenant.modules.records.toasts.deleted') });
      queryClient.invalidateQueries({ queryKey: ['module-records', moduleSlug] });
      queryClient.invalidateQueries({ queryKey: ['distinct-values', distinctTableId] });
      setSelectedIds(new Set());
    },
    onError: () => {
      toast.show({ variant: 'error', title: t('tenant.modules.records.toasts.deleteError') });
    },
  });

  // Wyciągnięcie aktywnych pól + form layout
  const activeFields = useMemo(() => {
    if (!moduleQuery.data) return [];
    return moduleQuery.data.fields.filter(
      (f) => !('gcrecord' in f && (f as { gcrecord?: string }).gcrecord),
    );
  }, [moduleQuery.data]);

  // useColumnConfig: widoczne kolumny z localStorage (F12.E T12.E.9)
  const visibleColumns = useColumnConfig(moduleId, activeFields);

  // Opcje filtra wiersza filtrów, które trzeba wczytać: użytkownicy (pola „użytkownik”), rekordy
  // celów relacji (pola „relacja”) i pozycje słowników (pola wyboru ze słownikiem) — patrz
  // `useModuleListFilterOptions`. Podpowiedzi relacji liczone z rekordów całej kartoteki.
  const filterOptions = useModuleListFilterOptions({
    tableId: distinctTableId,
    fields: visibleColumns,
    // Bez wczytanego modułu (pusty id) adresu nie ma — `buildEndpointUrl` zbudowałby `/modules//…`.
    relationDistinctUrl: (slug) =>
      moduleId === '' ? null : buildEndpointUrl(distinctTableId, slug),
  });

  // Czy którykolwiek typ rekordu ma włączoną numerację — wtedy kolumna „Numer"
  const hasNumbering = useMemo(
    () => moduleHasNumbering(categoriesQuery.data ?? []),
    [categoriesQuery.data],
  );

  // Kolumny dla ConfigurableTable (wspólny builder kolumn kartoteki własnej — te same kolumny i
  // kontrolki filtra pokazują inne ekrany tej kartoteki). Każda kolumna z danymi ma w wierszu
  // filtrów działającą kontrolkę wg typu pola albo pustą komórkę.
  const tableColumns = useMemo(
    () =>
      buildModuleListColumns<ModuleRecord>({
        t,
        fields: visibleColumns,
        categories: categoriesQuery.data ?? [],
        hasNumbering,
        sortableSlugs,
        filterOptions,
      }),
    [t, hasNumbering, categoriesQuery.data, visibleColumns, sortableSlugs, filterOptions],
  );

  // Kolumny dla ujednoliconej tablicy Kanban (id = slug, jak `tableColumns`) — osobna lista z
  // WSZYSTKICH pól i z dotychczasowymi regułami (opcje pola wyboru = tory), więc opcje dołożone
  // wierszowi filtrów nie zmieniają torów (patrz `buildModuleKanbanColumns`).
  const kanbanColumns = useMemo(
    () => buildModuleKanbanColumns({ t, fields: activeFields }),
    [t, activeFields],
  );

  // Kanban: przesuń rekord do innej kolumny (zmień wartość pola grupującego).
  // `KanbanBoard` woła z `(rowId, groupByFieldId, newValue)` — groupByFieldId to
  // slug pola, więc PUT-ujemy je wprost.
  const handleRowUpdate = useCallback(
    async (recordId: string, fieldSlug: string, newValue: string | null) => {
      await api.put(`/modules/${moduleId}/records/${recordId}`, {
        data: { [fieldSlug]: newValue ?? '' },
      });
      void queryClient.invalidateQueries({ queryKey: ['module-records-kanban', moduleSlug] });
    },
    [moduleId, moduleSlug, queryClient],
  );

  // Eksport CSV (E16) — dokładnie to, co lista. „Eksport listy": te same parametry filtrów co
  // zapytanie listy (sort, wyszukiwarka, typ, archiwalne, filtry kolumn, szybki filtr, filtry stałe
  // z menu), bez stronicowania. „Eksport zaznaczonych": tylko wskazane ID (+ sort listy).
  // Plik zawiera WYŁĄCZNIE kolumny widoczne w tabeli, w jej kolejności i z jej nagłówkami;
  // wartości wylicza ekran (etykiety słowników/użytkowników/relacji/kategorii), serwer oddaje
  // surowe wiersze tylko tych kolumn.
  const listExport = useListCsvExport<ModuleRecord>({
    filename: moduleQuery.data?.slug ?? 'export',
    formattingRules: listLayout?.layoutConfig?.formattingRules,
    heading: pageConfig?.title?.trim() || moduleQuery.data?.name,
    // Lista kolumn ustalana RAZ w chwili kliknięcia i niesiona przez cały eksport (żądanie + plik).
    // Dopóki moduł, układ listy i widoczność kolumn się ładują, eksport nie startuje.
    getColumnIds: () =>
      moduleQuery.isSuccess && !layoutLoading && visibleColumnIds !== null
        ? exportColumnsFromTable(tableColumns, visibleColumnIds).map((c) => c.id)
        : null,
    buildColumns: (_response, columnIds) =>
      exportColumnsFromTable(tableColumns, columnIds, {
        textOverrides: moduleExportTextOverrides({
          fields: activeFields,
          categories: categoriesQuery.data ?? [],
          filterOptions,
        }),
        customLabels: Object.fromEntries(
          (listLayout?.layoutConfig?.columns ?? []).map((c) => [c.id, c.customLabel]),
        ),
      }),
    fetchRows: async (ids, columnIds) => {
      const params = buildListFilterParams();
      if (ids && ids.length > 0) {
        // Zaznaczone: same ID — bez filtrów widoku (rekordy już są wybrane), z sortem listy.
        for (const key of Array.from(params.keys())) {
          if (key !== 'sort' && key !== 'order') params.delete(key);
        }
        params.set('ids', ids.join(','));
      }
      params.set('columns', moduleExportKeys(columnIds).join(','));
      const res = await api.get<ListExportResponse<ModuleRecord>>(
        `${listExportPath('module', moduleId)}?${params}`,
      );
      return res.data;
    },
  });

  // Akcje listy (PHASE 25) — generyczny hook, kartoteka `generic`: na liście modułu
  // katalog pokazuje TYLKO akcje uniwersalne (archiwizuj/przywróć/eksport/import/
  // odśwież); KSeF/FK (zawężone do dokumentów) się nie pojawią. Archiwizacja =
  // rozgłoszenie DELETE po ID na istniejący per-rekordowy endpoint modułu
  // (`/modules/:moduleId/records/:id`), przywrócenie = POST `.../restore`. Eksport
  // CSV i import mają własny kontrakt (GET z filtrami / modal) → handlery strony.
  // `selectedIds` strony to `Set` — hook operuje na `string[]`, stąd adaptery.
  const selectedIdsArray = useMemo(() => Array.from(selectedIds), [selectedIds]);
  const setSelectedIdsArray = useCallback((ids: string[]) => setSelectedIds(new Set(ids)), []);
  const listActions = useListActions({
    kartotekaKey: 'generic',
    entity: `modules/${moduleId}/records`,
    queryKey: ['module-records', moduleSlug],
    // Distinct values (dropdowny filtrów) tej listy są kluczowane po `distinctTableId`
    // (`module-<id>`, bez sufiksu układu), NIE po `entity` — spójnie z pojedynczym delete.
    distinctValuesKey: ['distinct-values', distinctTableId],
    selectedIds: selectedIdsArray,
    setSelectedIds: setSelectedIdsArray,
    listActionsConfig: listLayout?.layoutConfig?.listActions,
    customButtons: listLayout?.layoutConfig?.customActionButtons,
    handlers: {
      export_csv: (ids) => listExport.exportSelected(ids),
      export_list_csv: () => listExport.exportList(),
      import: () => setShowImportModal(true),
    },
    // „Pokaż zarchiwizowane" żyje w menu Akcje (nie w pasku nad listą) — decyzja Eugenii:
    // pasek nad tabelą nie trzyma filtrów, od tego są Akcje.
    archivedView: includeArchived,
    archivedToggle: {
      active: includeArchived,
      onToggle: () => {
        setIncludeArchived(!includeArchived);
        controls.resetPage();
      },
    },
    testIdPrefix: 'records',
  });

  // ─── Render guards ────────────────────────────────────────────────────────

  if (!moduleSlug) {
    return <main className="p-6">{t('tenant.modules.records.noSlug')}</main>;
  }

  if (moduleQuery.isLoading) {
    return (
      <main className="flex min-h-full items-center justify-center bg-bg-secondary p-6">
        <Spinner />
      </main>
    );
  }

  if (moduleQuery.isError || !moduleQuery.data) {
    return (
      <main className="bg-bg-secondary p-6">
        <Card padding="md">
          <p className="text-error-500">{t('tenant.modules.records.loadError')}</p>
        </Card>
      </main>
    );
  }

  const module = moduleQuery.data;
  const records = recordsQuery.data?.records ?? [];
  const total = recordsQuery.data?.pagination.total ?? 0;

  // ─── Row delete (widok galerii) ───────────────────────────────────────────

  function handleDeleteOne(record: ModuleRecord) {
    setPendingConfirm({
      title: t('common.delete'),
      message: t('tenant.modules.records.confirmDelete', {
        name: record.record_name ?? record.id.slice(0, 8),
      }),
      onConfirm: () => deleteMutation.mutate(record.id),
    });
  }

  // Wysokość/scroll „przyklejony" (nagłówek + paski nieskalujące, scroll tylko w tabeli)
  // dotyczy WYŁĄCZNIE widoku tabeli — 1:1 z Kontrahentami/Pracownikami (`Contractors.tsx`).
  // Kanban/galeria/kalendarz nie mają odpowiednika w kartotekach systemowych i zostają
  // przy dotychczasowym zachowaniu (cała strona się przewija).
  const isTableView = viewMode === 'table';

  // Zestaw trybów przełącznika widoku — Lista i Galeria zawsze, Kalendarz gdy moduł ma pole
  // daty, Kanban gdy skonfigurowany w Kreatorze Listy. Ten sam wspólny komponent co kartoteki.
  const viewModeOptions: ViewModeOption<ModuleRecordsViewMode>[] = [
    { mode: 'table', Icon: LayoutList, title: t('tenant.modules.kanban.viewTable') },
    { mode: 'gallery', Icon: Grid3X3, title: t('tenant.modules.gallery.viewGallery') },
  ];
  if (activeFields.some((f) => f.fieldType === 'date' || f.fieldType === 'datetime')) {
    viewModeOptions.push({
      mode: 'calendar',
      Icon: CalendarDays,
      title: t('tenant.modules.calendar.viewCalendar'),
    });
  }
  if (hasKanban) {
    viewModeOptions.push({
      mode: 'kanban',
      Icon: Trello,
      title: t('tenant.modules.kanban.viewKanban'),
    });
  }

  return (
    <main
      className={
        isTableView
          ? 'flex h-full min-h-0 flex-col bg-bg-secondary p-6'
          : 'min-h-full bg-bg-secondary p-6'
      }
      data-testid="module-records-page"
    >
      <div className={isTableView ? 'flex min-h-0 flex-1 flex-col gap-4' : 'space-y-4'}>
        {/* Header */}
        <header className="flex shrink-0 flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={() => navigate('/modules')}
              aria-label={t('common.back')}
              className="rounded-md p-1 text-text-muted hover:bg-bg-tertiary"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="min-w-0">
              <h1 className="truncate text-2xl font-bold text-text-primary">
                {pageConfig?.title?.trim() || module.name}
              </h1>
              {pageConfig?.description?.trim() ? (
                <p className="text-sm text-text-secondary">{pageConfig.description}</p>
              ) : null}
              <p className="text-xs text-text-tertiary">
                {t('tenant.modules.records.totalCount', { count: total })}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Przełącznik widoku — WSPÓLNY komponent (pastylka) co kartoteki systemowe,
                z modułowym zestawem trybów (patrz `viewModeOptions`). */}
            <ListViewModeToggle
              value={viewMode}
              onChange={setViewMode}
              testIdPrefix="records"
              modes={viewModeOptions}
            />
            <ListActionsMenu {...listActions.menuProps} />
            {/* Zarządzanie kategoriami przeniesione do Kreatora formularza (kategoria = pole
                jednokrotnego wyboru rekordu) — osobnego przycisku „Kategorie" tu nie ma. */}
            <ListBuilderMenu
              listPath={`/modules/${moduleSlug}/kreator-listy`}
              formPath={`/modules/${module.id}/builder`}
              activeLayoutTableId={distinctTableId}
              testIdPrefix="records"
            />
            <Button
              variant="primary"
              leftIcon={<Plus className="h-4 w-4" />}
              onClick={() => navigate(withMenuItem(`/modules/${moduleSlug}/records/new`))}
              data-testid="records-new-btn"
            >
              {labelFor('create', t('tenant.modules.records.newRecord'))}
            </Button>
          </div>
        </header>

        <div className="shrink-0">
          <ListTopWidgets tableId={tableId} categoryId={activeTypeId} />
        </div>

        {/* Tabs per type (tylko gdy moduł ma typy z showAsTab=true) */}
        {(categoriesQuery.data?.filter((rt) => rt.showAsTab) ?? []).length > 0 && (
          <div className="flex shrink-0 flex-wrap gap-1 border-b border-border-primary pb-0">
            <button
              type="button"
              onClick={() => {
                setActiveTypeId(null);
                controls.resetPage();
              }}
              className={`rounded-t-md px-4 py-2 text-sm font-medium transition-colors ${
                activeTypeId === null
                  ? 'border-b-2 border-primary-600 text-primary-700'
                  : 'text-text-muted hover:text-text-primary'
              }`}
            >
              {t('tenant.modules.records.allTypes')}
            </button>
            {categoriesQuery
              .data!.filter((rt) => rt.showAsTab)
              .map((rt) => (
                <button
                  key={rt.id}
                  type="button"
                  onClick={() => {
                    setActiveTypeId(rt.id);
                    controls.resetPage();
                  }}
                  className={`flex items-center gap-1.5 rounded-t-md px-4 py-2 text-sm font-medium transition-colors ${
                    activeTypeId === rt.id
                      ? 'border-b-2 text-current'
                      : 'text-text-muted hover:text-text-primary'
                  }`}
                  style={
                    activeTypeId === rt.id ? { borderColor: rt.color, color: rt.color } : undefined
                  }
                >
                  <span
                    className="inline-block h-2 w-2 rounded-full"
                    style={{ backgroundColor: rt.color }}
                  />
                  {rt.name}
                </button>
              ))}
          </div>
        )}

        {/* Calendar view */}
        {viewMode === 'calendar' && (
          <Card padding="md">
            <CalendarView
              records={calendarRecordsQuery.data ?? []}
              fields={activeFields}
              dateFieldSlug={calendarConfig?.startFieldSlug}
              endDateFieldSlug={calendarConfig?.endFieldSlug}
              statusFieldSlug={calendarConfig?.statusFieldSlug}
              isLoading={calendarRecordsQuery.isLoading}
              onRecordClick={(record) =>
                navigate(withMenuItem(`/modules/${moduleSlug}/${record.id}`))
              }
              onDayClick={(_date) => navigate(withMenuItem(`/modules/${moduleSlug}/records/new`))}
            />
          </Card>
        )}

        {/* Gallery view */}
        {viewMode === 'gallery' && (
          <Card padding="md">
            <GalleryView
              moduleId={module.id}
              records={records}
              fields={activeFields}
              isLoading={recordsQuery.isLoading}
              onRecordClick={(record) =>
                navigate(withMenuItem(`/modules/${moduleSlug}/${record.id}`))
              }
              onRecordDelete={handleDeleteOne}
            />
          </Card>
        )}

        {/* Kanban view — ujednolicona tablica (wspólna z listami systemowymi) */}
        {viewMode === 'kanban' && hasKanban && (
          <Card className="p-4">
            {kanbanRecordsQuery.isLoading ? (
              <div className="flex justify-center py-16">
                <Spinner />
              </div>
            ) : (
              <KanbanBoard<ModuleRecord>
                testIdPrefix="records"
                columns={kanbanColumns}
                data={kanbanRecordsQuery.data ?? []}
                getRowId={(row) => row.id}
                getCellValue={(row, column) =>
                  typeof column.accessor === 'function'
                    ? column.accessor(row)
                    : (row as Record<string, unknown>)[column.accessor]
                }
                getColumnConfig={() => undefined}
                kanbanConfig={kanbanConfig}
                onRowClick={(row) => navigate(withMenuItem(`/modules/${moduleSlug}/${row.id}`))}
                onRowUpdate={handleRowUpdate}
              />
            )}
          </Card>
        )}

        {/* Records table */}
        {viewMode === 'table' && recordsQuery.isError && (
          <p className="shrink-0 px-1 py-2 text-error-500">
            {t('tenant.modules.records.loadError')}
          </p>
        )}
        {viewMode === 'table' && (
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <ConfigurableTable<ModuleRecord>
              tableId={tableId}
              distinctTableId={distinctTableId}
              testIdPrefix="records"
              showOrdinal
              showViewModeToggle={false}
              fillHeight
              /* Górny pasek listy WEWNĄTRZ karty tabeli (parytet z Figmą): segmented po lewej,
                 pasek filtrów (search + odśwież + archiwalne) po prawej. Przełącznik widoku,
                 Akcje i Kreator zostają w nagłówku strony — muszą być widoczne też w widokach
                 kalendarza/galerii/kanbana, które żyją poza tabelą. */
              listToolbar={
                <div className="flex flex-wrap items-center gap-2">
                  <QuickFilterBar
                    quickFilters={quickFilters}
                    activeId={activeQuickFilterId}
                    onChange={setActiveQuickFilterId}
                  />
                  {pageConfig?.showFilterBar !== false && (
                    <div className="ml-auto shrink-0">
                      <ListFilterBar
                        tableId={tableId}
                        columns={tableColumns}
                        search={search}
                        onSearchChange={(next) => {
                          setSearch(next);
                          controls.resetPage();
                        }}
                        searchPlaceholder={t('tenant.modules.recordsList.searchPlaceholder')}
                        // Pole wyszukiwania i „Pokaż zarchiwizowane" NIE żyją w pasku nad tabelą
                        // (decyzja Eugenii): szukanie robią filtry w nagłówkach kolumn, a
                        // przełącznik archiwum przeniesiony do menu Akcje (patrz `archivedToggle`).
                        showSearch={false}
                        headerFilters={headerFilters}
                        onHeaderFiltersChange={setHeaderFilters}
                        onRefresh={() =>
                          queryClient.invalidateQueries({
                            queryKey: ['module-records', moduleSlug],
                          })
                        }
                        testIdPrefix="records"
                      />
                    </div>
                  )}
                </div>
              }
              columns={tableColumns}
              data={records}
              isLoading={recordsQuery.isLoading}
              filterable
              headerFilters={headerFilters}
              onHeaderFiltersChange={setHeaderFilters}
              selectable
              selectedIds={Array.from(selectedIds)}
              onSelectionChange={(ids) => setSelectedIds(new Set(ids))}
              onVisibleColumnsChange={setVisibleColumnIds}
              getRowId={(row) => row.id}
              {...controls.tableProps}
              totalItems={total}
              onRowClick={(row) => navigate(withMenuItem(`/modules/${moduleSlug}/${row.id}`))}
              onRowContextMenu={listActions.onRowContextMenu}
              renderActions={(row) => (
                <div className="flex justify-end">
                  <RowActionsMenu
                    testId={`records-row-${row.id}-actions`}
                    items={[
                      {
                        key: 'edit',
                        label: t('common.edit'),
                        icon: Pencil,
                        onSelect: () => navigate(withMenuItem(`/modules/${moduleSlug}/${row.id}`)),
                        testId: `records-row-${row.id}-edit`,
                      },
                      // Akcje katalogu na TYM rekordzie (pod dividerem): archiwizuj,
                      // eksport, … (bez `restore` — bez sensu na żywym wierszu).
                      ...listActions.rowCatalogActionItems(row.id),
                    ]}
                  />
                </div>
              )}
              emptyIcon={LayoutList}
              emptyTitle={t('tenant.modules.records.empty.title')}
              emptyDescription={t('tenant.modules.records.empty.subtitle')}
              emptyAction={
                <Button
                  variant="primary"
                  leftIcon={<Plus className="h-4 w-4" />}
                  onClick={() => navigate(withMenuItem(`/modules/${moduleSlug}/records/new`))}
                  data-testid="records-empty-new-btn"
                >
                  {t('tenant.modules.records.newRecord')}
                </Button>
              }
            />
          </div>
        )}
      </div>

      {/* Import modal */}
      {showImportModal && (
        <RecordImportModal
          moduleId={module.id}
          moduleSlug={module.slug}
          onClose={() => setShowImportModal(false)}
          onSuccess={() => {
            setShowImportModal(false);
            controls.resetPage();
            void queryClient.invalidateQueries({ queryKey: ['module-records', moduleSlug] });
          }}
        />
      )}

      {pendingConfirm && (
        <ConfirmDialog
          open={true}
          title={pendingConfirm.title}
          message={pendingConfirm.message}
          confirmLabel={t('common.delete')}
          onConfirm={() => {
            pendingConfirm.onConfirm();
            setPendingConfirm(null);
          }}
          onClose={() => setPendingConfirm(null)}
        />
      )}

      {listActions.overlays}
    </main>
  );
}
